/**
 * HereMenu brand logo (admin panel copy). Mirrors the dashboard component
 * so the mark stays identical across surfaces; admin panel adds an
 * optional "Admin" suffix badge to differentiate the surface without
 * changing the wordmark itself.
 */
type Size = "sm" | "md" | "lg" | "xl";
type Variant = "full" | "mark";

const sizes: Record<Size, { mark: number; text: string; badge: string }> = {
  sm: { mark: 20, text: "text-sm", badge: "text-[9px] px-1.5 py-0.5" },
  md: { mark: 32, text: "text-lg", badge: "text-[10px] px-1.5 py-0.5" },
  lg: { mark: 44, text: "text-2xl", badge: "text-xs px-2 py-0.5" },
  xl: { mark: 56, text: "text-3xl", badge: "text-xs px-2 py-1" },
};

interface LogoProps {
  size?: Size;
  variant?: Variant;
  suffix?: string;
  className?: string;
}

export function Logo({ size = "md", variant = "full", suffix, className = "" }: LogoProps) {
  const s = sizes[size];
  return (
    <div className={`inline-flex items-center gap-2.5 ${className}`}>
      <LogoMark px={s.mark} />
      {variant === "full" && (
        <span className={`font-bold tracking-tight leading-none ${s.text}`}>
          <span className="text-slate-900">here</span>
          <span className="text-[#047857]">menu</span>
        </span>
      )}
      {suffix && (
        <span
          className={`uppercase tracking-wider font-semibold rounded-md bg-slate-100 text-slate-600 ${s.badge}`}
        >
          {suffix}
        </span>
      )}
    </div>
  );
}

function LogoMark({ px }: { px: number }) {
  return (
    <svg
      width={px}
      height={px}
      viewBox="0 0 100 100"
      xmlns="http://www.w3.org/2000/svg"
      className="shrink-0"
      aria-label="HereMenu"
      role="img"
    >
      <rect x="6" y="6" width="88" height="88" rx="20" fill="#047857" />
      <rect x="20" y="22" width="60" height="56" rx="6" fill="#fafaf9" />
      <rect x="28" y="32" width="44" height="6" rx="2" fill="#0f172a" />
      <rect x="28" y="46" width="36" height="4" rx="2" fill="#334155" />
      <rect x="28" y="56" width="44" height="4" rx="2" fill="#334155" />
      <rect x="28" y="66" width="28" height="4" rx="2" fill="#334155" />
    </svg>
  );
}
